/**
 * Cliente API para GET /api/admin/sessions/:id/learning — todo lo que el
 * destilado produjo para una sesión (vocab, errores, correcciones, métricas
 * CEFR, objetivos de escenario). Los memory_facts se traen aparte vía
 * `user-memory.ts` (`fetchUserMemoryFacts` con `session_id`).
 */
import { get } from './_core.js';

export interface LearningVocabItem {
  id: string;
  text: string;
  translation: string | null;
  entryType: string;
  partOfSpeech: string | null;
  cefr: string | null;
  ipa: string | null;
  topic: string | null;
  example: string | null;
  createdAt: string;
}

export interface LearningErrorPatternItem {
  id: string;
  pattern: string;
  category: string | null;
  example: string | null;
  dimension: string | null;
  suggestion: string | null;
  confidence: string | null;
  createdAt: string;
}

export interface LearningCorrectionItem {
  id: string;
  turnIndex: number | null;
  userUtterance: string;
  correctedForm: string;
  explanation: string | null;
  category: string | null;
  /** 'minor' | 'structural' | 'communication-breaking' | 'suggestion' */
  severity: string;
  confidence: string | null;
  createdAt: string;
}

export interface LearningMetrics {
  mode: string;
  intensity: string;
  englishLevel: number;
  unitId: string | null;
  cefrEstimate: string | null;
  paAvgAccuracy: string | null;
  paAvgFluency: string | null;
  paAvgProsody: string | null;
  paWordsEvaluated: number;
  paErrorsCount: number;
  distilledAt: string | null;
}

export interface LearningScenarioObjectiveItem {
  id: string;
  scenarioId: string;
  objectiveId: string;
  met: boolean;
  evidence: string | null;
  confidence: string | null;
  createdAt: string;
}

export interface SessionLearningResponse {
  meta: {
    userId: string;
    userEmail: string;
    moduleId: string | null;
    startedAt: string;
    endedAt: string | null;
  };
  vocab: LearningVocabItem[];
  errorPatterns: LearningErrorPatternItem[];
  corrections: LearningCorrectionItem[];
  metrics: LearningMetrics | null;
  scenarioObjectives: LearningScenarioObjectiveItem[];
}

export async function fetchSessionLearning(
  sessionId: string,
): Promise<SessionLearningResponse> {
  const res = await get<SessionLearningResponse>(
    `/admin/sessions/${encodeURIComponent(sessionId)}/learning`,
  );
  return res.data;
}
